/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/no-explicit-any, @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import { useRecoilValue } from 'recoil';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsPopout from '../../shared/SettingsPopout';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { Dayjs } from 'dayjs';
import { currentStatsState } from 'states/iVol';
import { useCallback, useMemo, useState } from 'react';
import CropFreeIcon from '@mui/icons-material/CropFree';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import useIsMobile from 'hooks/useIsMobile';
import {
  dayjs,
  getCurrentDate,
  getDefaultSlotProps,
  getQueryDate,
  isBusinessDay,
  isMarketOpen,
  isMarketOpenOnDate,
  nextBusinessDay,
  prevBusinessDayOpenMarket,
} from 'util/shared';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import {
  DashAccess,
  FixedStrikeZoomLevel,
  hasDashAccess,
  MATRIX_EXPIRATIONS_RANGE_MAPPING,
  MATRIX_PERCENT_OTM_RANGE_MAPPING,
  MatrixDataMode,
  MatrixExpirationsRange,
  MatrixPercentOTMRange,
  PRUNE_PERCENT_MAPPING,
  PrunePercentRange,
  RawGreeksDataMap,
  RawStatsData,
} from 'types';
import {
  DEFAULT_STATS_LOOKBACK,
  MATRIX_DATA_SETTING_TOOLTIPS,
  MIN_QUERY_DATE,
  buildMatrixTableData,
  findClosestGreeksExp,
  findD50Price,
  getExpStatsMap,
} from 'util/iVol';
import InfoPopper from '../../shared/InfoPopper';
import { userDashAccessState } from 'states';
import StatsLookbackSettings from '../StatsLookbackSetting';
import useClosingPrices from 'hooks/equityhub/useClosingPrices';
import { useFixedStrikeCtx } from './FixedStrikeContext';
import { useDividends } from 'hooks/useDividends';
import { transformDividendsToRiskEngineFormat } from 'util/dividendUtils';
import useRiskFreeRate from 'hooks/riskFreeRate/useRiskFreeRate';

const FixedStrikeControls = () => {
  const {
    config,
    updateConfig: onConfigChange,
    setCurrentData: setMatrixData,
    setColumnData: setMatrixColumnData,
    currentGreeks: fixedStrikeCurrentGreeks,
    setCurrentGreeks: setFixedStrikeCurrentGreeks,
    prevExpiryMap: matrixPrevExpiryMap,
    setPrevExpiryMap: setMatrixPrevExpiryMapState,
    matrixExpirationRangeOpen,
    setMatrixExpirationRangeOpen,
    matrixPercentOTMRangeOpen,
    setMatrixPercentOTMRangeOpen,
    pruneRowsPercentSelectorOpen,
    setPruneRowsPercentSelectorOpen,
    pruneColumnsPercentSelectorOpen,
    setPruneColumnsPercentSelectorOpen,
    currentDate,
    setCurrentDate,
    compareDate,
    setCompareDate,
    loading,
    error,
  } = useFixedStrikeCtx();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const defaultSlotProps = getDefaultSlotProps(theme, isMobile);
  const dashAccess = useRecoilValue(userDashAccessState);

  const selectedSym = config.sym;
  const matrixDataMode = config.dataMode;
  const showHighlights = config.showHighlights;
  const useSpotGammaIV = config.useSpotGammaIV;
  const showHistoricalData = config.showHistoricalData;
  const matrixZoomLevel = config.zoomLevel;
  const matrixExpirationRange = config.expirationRange;
  const matrixPercentOTMRange = config.percentOTMRange;
  const pruneRowsPercent = config.pruneRowsPercent;
  const pruneColumnsPercent = config.pruneColumnsPercent;
  const showDividends = config.showDividends;

  const currentStats = useRecoilValue(currentStatsState);
  const currentStatsLookback = config.statsLookback;

  const statsDataObj: RawStatsData | null =
    currentStats &&
    (currentStats[currentStatsLookback] ??
      currentStats[DEFAULT_STATS_LOOKBACK]);

  const [currentDateHelperText, setCurrentDateHelpText] = useState<
    string | null
  >(null);
  const [compareDateHelperText, setCompareDateHelperText] = useState<
    string | null
  >(null);

  const { data: dividendsData } = useDividends({ sym: selectedSym });
  const { dividendDates, dividendAmounts } = useMemo(
    () => transformDividendsToRiskEngineFormat(dividendsData ?? []),
    [dividendsData],
  );

  const { getRiskFreeRateForYte } = useRiskFreeRate(currentDate);

  const handleExpirationChange = (event: SelectChangeEvent) => {
    onConfigChange({
      expirationRange: event.target.value as MatrixExpirationsRange,
    });
  };

  const handlePercentOTMChange = (event: SelectChangeEvent) => {
    onConfigChange({
      percentOTMRange: event.target.value as MatrixPercentOTMRange,
    });
  };

  const handlePruneRowsChange = (event: SelectChangeEvent) => {
    onConfigChange({
      pruneRowsPercent: event.target.value as PrunePercentRange,
    });
  };

  const handlePruneColumnsChange = (event: SelectChangeEvent) => {
    onConfigChange({
      pruneColumnsPercent: event.target.value as PrunePercentRange,
    });
  };

  const { getCurrentGreeksData, getDailyGreeksData } = useImpliedVolatility();
  const { getClosingPrice } = useClosingPrices();

  const maxCompareDate = prevBusinessDayOpenMarket();
  const maxCurrentDate = getQueryDate(true);

  const isSelectedDateValid = useCallback((date: Dayjs, maxDate: Dayjs) => {
    return date?.isValid() && date?.isBefore(nextBusinessDay(maxDate), 'day');
  }, []);

  const isCompareMode = useMemo(
    () => matrixDataMode === MatrixDataMode.COMPARE_MODE,
    [matrixDataMode],
  );

  const isStatsMode = useMemo(
    () => matrixDataMode === MatrixDataMode.STATS_MODE,
    [matrixDataMode],
  );

  const isSkewPremMode = useMemo(
    () => matrixDataMode === MatrixDataMode.SKEW_PREMIUM_MODE,
    [matrixDataMode],
  );

  const onPrevTradeDateChange = useCallback(
    async (newValue: Dayjs | null) => {
      if (newValue && isSelectedDateValid(newValue, maxCompareDate)) {
        // update selected date
        setCompareDate(newValue);
        const formattedDate: string = newValue.format('YYYY-MM-DD');
        // fetch the relevant greeks based on the selected date
        const [rawData, currentPrice] = await Promise.all([
          getDailyGreeksData(formattedDate, selectedSym),
          getClosingPrice(selectedSym),
        ]);

        if (rawData && statsDataObj && fixedStrikeCurrentGreeks) {
          const expStatsMap = getExpStatsMap(
            statsDataObj,
            fixedStrikeCurrentGreeks,
          );

          // rebuild table with updated prev raw greeks data map
          const { matrixData, matrixColumnData } = buildMatrixTableData(
            currentDate,
            fixedStrikeCurrentGreeks,
            isCompareMode,
            rawData,
            expStatsMap,
            currentPrice ??
              findD50Price(
                fixedStrikeCurrentGreeks[
                  findClosestGreeksExp(fixedStrikeCurrentGreeks, newValue)
                ],
              ),
            hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
            getRiskFreeRateForYte,
            dividendDates,
            dividendAmounts,
          );

          setMatrixData(matrixData);
          setMatrixColumnData(matrixColumnData);
          setMatrixPrevExpiryMapState(rawData);
          setCompareDateHelperText(null);
        } else {
          setCompareDateHelperText(
            'Failed to fetch data for your selected date. Please try again or choose a different date!',
          );
        }
      } else {
        setCompareDateHelperText(
          'Invalid compare date selection. Please try again!',
        );
      }
    },
    [
      isSelectedDateValid,
      maxCompareDate,
      currentDate,
      setCompareDate,
      getDailyGreeksData,
      selectedSym,
      fixedStrikeCurrentGreeks,
      statsDataObj,
      isCompareMode,
      setMatrixData,
      setMatrixColumnData,
      setMatrixPrevExpiryMapState,
      useSpotGammaIV,
    ],
  );

  const onCurrentTradeDateChange = useCallback(
    async (newValue: Dayjs | null) => {
      if (newValue && isSelectedDateValid(newValue, maxCurrentDate)) {
        // update selected date
        setCurrentDate(newValue);
        const formattedDate: string = newValue.format('YYYY-MM-DD');
        const isCurrentDay = newValue.isSame(getCurrentDate(), 'day');

        const [rawData, currentPrice] = await Promise.all([
          isCurrentDay
            ? getCurrentGreeksData(selectedSym)
            : getDailyGreeksData(formattedDate, selectedSym),
          getClosingPrice(
            selectedSym,
            isCurrentDay ? undefined : formattedDate,
          ),
        ]);

        if (rawData && statsDataObj) {
          const expStatsMap = getExpStatsMap(statsDataObj, rawData);

          // rebuild table with updated current raw greeks data map
          const { matrixData, matrixColumnData } = buildMatrixTableData(
            newValue,
            rawData,
            isCompareMode,
            matrixPrevExpiryMap,
            expStatsMap,
            currentPrice ??
              findD50Price(rawData[findClosestGreeksExp(rawData, newValue)]),
            hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
            getRiskFreeRateForYte,
            dividendDates,
            dividendAmounts,
          );

          setMatrixData(matrixData);
          setMatrixColumnData(matrixColumnData);
          setCurrentDateHelpText(null);
          setFixedStrikeCurrentGreeks(rawData);
        } else {
          setCurrentDateHelpText(
            'Failed to fetch data for your selected date. Please try again or choose a different date!',
          );
        }
      } else {
        setCurrentDateHelpText('Invalid date selection. Please try again!');
      }
    },
    [
      isSelectedDateValid,
      maxCurrentDate,
      setCurrentDate,
      getCurrentGreeksData,
      selectedSym,
      statsDataObj,
      getDailyGreeksData,
      isCompareMode,
      matrixPrevExpiryMap,
      setMatrixData,
      setMatrixColumnData,
      useSpotGammaIV,
    ],
  );

  const getNeighborDate = (date: Dayjs) => {
    const prevBusinessDay = prevBusinessDayOpenMarket(date);
    const followingBusinessDay = nextBusinessDay(date);

    if (
      prevBusinessDay.isSame(MIN_QUERY_DATE, 'day') ||
      prevBusinessDay.isAfter(MIN_QUERY_DATE, 'day')
    ) {
      return prevBusinessDay;
    }

    if (
      followingBusinessDay.isSame(MIN_QUERY_DATE, 'day') ||
      followingBusinessDay.isBefore(maxCompareDate, 'day')
    ) {
      return followingBusinessDay;
    }

    return date;
  };

  const onCompareModeToggle = useCallback(
    async (_e: any, checked: boolean) => {
      onToggleDataSettings(MatrixDataMode.COMPARE_MODE, checked);
      if (checked) {
        let correctCompareDate = compareDate;
        if (currentDate.isSame(MIN_QUERY_DATE, 'day')) {
          onCurrentTradeDateChange(nextBusinessDay(MIN_QUERY_DATE));
        } else {
          if (
            showHistoricalData &&
            (compareDate.isSame(currentDate, 'day') ||
              compareDate.isAfter(currentDate, 'day'))
          ) {
            correctCompareDate = getNeighborDate(currentDate);
            setCompareDate(correctCompareDate);
          }
        }
        const formattedDate: string = correctCompareDate.format('YYYY-MM-DD');
        // fetch the relevant greeks based on the selected date
        const rawData: RawGreeksDataMap | null = await getDailyGreeksData(
          formattedDate,
          selectedSym,
        );
        const currentPrice = await getClosingPrice(selectedSym, formattedDate);

        // raw data is compare date daily greeks data
        if (rawData && fixedStrikeCurrentGreeks) {
          const expStatsMap =
            statsDataObj && getExpStatsMap(statsDataObj, rawData);

          // rebuild table with updated current raw greeks data map
          const { matrixData, matrixColumnData } = buildMatrixTableData(
            currentDate,
            fixedStrikeCurrentGreeks,
            checked,
            rawData,
            expStatsMap,
            currentPrice ??
              findD50Price(
                rawData[findClosestGreeksExp(rawData, correctCompareDate)],
              ),
            hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
            getRiskFreeRateForYte,
            dividendDates,
            dividendAmounts,
          );

          setMatrixData(matrixData);
          setMatrixColumnData(matrixColumnData);

          // update prev raw greeks data map
          setMatrixPrevExpiryMapState(rawData);
        }
      }
    },
    [
      compareDate,
      setCompareDate,
      getDailyGreeksData,
      selectedSym,
      currentDate,
      fixedStrikeCurrentGreeks,
      statsDataObj,
      setMatrixData,
      setMatrixColumnData,
      setMatrixPrevExpiryMapState,
      useSpotGammaIV,
    ],
  );

  const onHistoricalModeToggle = useCallback(
    async (_e: any, checked: boolean) => {
      onConfigChange({ showHistoricalData: checked });

      if (
        checked &&
        (compareDate.isSame(currentDate, 'day') ||
          compareDate.isAfter(currentDate, 'day'))
      ) {
        setCompareDate(getNeighborDate(currentDate));
      }
      const formattedDate = currentDate.format('YYYY-MM-DD');

      const [rawData, currentPrice] = await Promise.all([
        !checked
          ? getCurrentGreeksData(selectedSym)
          : getDailyGreeksData(formattedDate, selectedSym, isMarketOpen()),
        getClosingPrice(selectedSym, !checked ? undefined : formattedDate),
      ]);

      // If raw data was fetched successfully, process and set it
      if (rawData && statsDataObj) {
        const expStatsMap = getExpStatsMap(statsDataObj, rawData);
        const { matrixData, matrixColumnData } = buildMatrixTableData(
          currentDate,
          rawData,
          isCompareMode,
          matrixPrevExpiryMap,
          expStatsMap,
          currentPrice ??
            findD50Price(rawData[findClosestGreeksExp(rawData, currentDate)]),
          hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
          getRiskFreeRateForYte,
          dividendDates,
          dividendAmounts,
        );

        setMatrixData(matrixData);
        setMatrixColumnData(matrixColumnData);
        setCurrentDateHelpText(null);
        setFixedStrikeCurrentGreeks(rawData);
      }
    },
    [
      compareDate,
      setCompareDate,
      getDailyGreeksData,
      getCurrentGreeksData,
      selectedSym,
      currentDate,
      statsDataObj,
      setMatrixData,
      setMatrixColumnData,
      setCurrentDateHelpText,
      setFixedStrikeCurrentGreeks,
      isMarketOpen,
      getNeighborDate,
      useSpotGammaIV,
    ],
  );

  const onUseSpotGammaIVToggle = useCallback(
    async (_e: any, checked: boolean) => {
      onConfigChange({ useSpotGammaIV: checked });

      if (fixedStrikeCurrentGreeks && statsDataObj) {
        const expStatsMap = getExpStatsMap(
          statsDataObj,
          fixedStrikeCurrentGreeks,
        );
        const currentPrice = await getClosingPrice(
          selectedSym,
          showHistoricalData ? currentDate.format('YYYY-MM-DD') : undefined,
        );

        const { matrixData, matrixColumnData } = buildMatrixTableData(
          currentDate,
          fixedStrikeCurrentGreeks,
          isCompareMode,
          matrixPrevExpiryMap,
          expStatsMap,
          currentPrice ??
            findD50Price(
              fixedStrikeCurrentGreeks[
                findClosestGreeksExp(
                  fixedStrikeCurrentGreeks,
                  showHistoricalData ? currentDate : dayjs(),
                )
              ],
            ),
          hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
          getRiskFreeRateForYte,
          dividendDates,
          dividendAmounts,
        );

        setMatrixData(matrixData);
        setMatrixColumnData(matrixColumnData);
        setCurrentDateHelpText(null);
      }
    },
    [
      compareDate,
      setCompareDate,
      getDailyGreeksData,
      getCurrentGreeksData,
      selectedSym,
      currentDate,
      dividendDates,
      dividendAmounts,
      onConfigChange,
      setMatrixData,
      setMatrixColumnData,
      setCurrentDateHelpText,
      setFixedStrikeCurrentGreeks,
      isMarketOpen,
      getNeighborDate,
      useSpotGammaIV,
    ],
  );

  const onToggleDataSettings = (type: MatrixDataMode, checked: boolean) => {
    onConfigChange({ dataMode: checked ? type : MatrixDataMode.DEFAULT });
  };

  const onStatsLookbackChange = async (newStatsData: RawStatsData) => {
    if (!fixedStrikeCurrentGreeks) {
      return;
    }
    const expStatsMap = getExpStatsMap(newStatsData, fixedStrikeCurrentGreeks);
    const currentPrice = await getClosingPrice(
      selectedSym,
      showHistoricalData ? currentDate.format('YYYY-MM-DD') : undefined,
    );

    // rebuild table with updated current raw greeks data map
    const { matrixData, matrixColumnData } = buildMatrixTableData(
      currentDate,
      fixedStrikeCurrentGreeks,
      isCompareMode,
      matrixPrevExpiryMap,
      expStatsMap,
      currentPrice ??
        findD50Price(
          fixedStrikeCurrentGreeks[
            findClosestGreeksExp(
              fixedStrikeCurrentGreeks,
              showHistoricalData ? currentDate : dayjs(),
            )
          ],
        ),
      hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
      getRiskFreeRateForYte,
      dividendDates,
      dividendAmounts,
    );

    setMatrixData(matrixData);
    setMatrixColumnData(matrixColumnData);
  };

  const statsLookbackComponent = useMemo(() => {
    if (!isStatsMode) {
      return null;
    }

    return (
      <StatsLookbackSettings
        value={currentStatsLookback}
        onChange={(lookback) => {
          onConfigChange({ statsLookback: lookback });
          // Also rebuild the matrix table with the new lookback stats
          const newStatsDataObj: RawStatsData | null =
            currentStats &&
            (currentStats[lookback] ?? currentStats[DEFAULT_STATS_LOOKBACK]);
          if (newStatsDataObj) {
            onStatsLookbackChange(newStatsDataObj);
          }
        }}
      />
    );
  }, [
    isStatsMode,
    currentStatsLookback,
    onConfigChange,
    onStatsLookbackChange,
    currentStats,
  ]);

  const compareModeToggle = useMemo(() => {
    return (
      <FormControlLabel
        sx={{ marginLeft: isMobile ? 0 : '10px' }}
        control={
          <Switch
            sx={{
              '& .MuiSwitch-track': {
                opacity: 0.25,
                backgroundColor: theme.palette.success.main,
              },
              '&.Mui-checked .MuiSwitch-track': {
                opacity: 1,
              },
            }}
            color="success"
            checked={isCompareMode}
            onChange={onCompareModeToggle}
          />
        }
        label={MatrixDataMode.COMPARE_MODE}
        labelPlacement={isMobile ? 'start' : 'top'}
      />
    );
  }, [isCompareMode, isMobile]);

  const showHistoricalDataToggle = useMemo(() => {
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          sx={{
            cursor: 'default',
          }}
        >
          Current
        </Typography>
        <Tooltip
          title={
            showHistoricalData
              ? 'Displaying historical greeks data using market close prices'
              : 'Displaying latest greeks data live'
          }
          PopperProps={{ style: { zIndex: 10_001 } }}
        >
          <Switch
            sx={{
              '& .MuiSwitch-track': {
                opacity: 0.25,
                backgroundColor: theme.palette.success.main,
              },
              '&.Mui-checked .MuiSwitch-track': {
                opacity: 1,
              },
            }}
            color="success"
            checked={showHistoricalData}
            onChange={onHistoricalModeToggle}
          />
        </Tooltip>

        <Typography
          sx={{
            cursor: 'default',
          }}
        >
          Historical
        </Typography>
      </Stack>
    );
  }, [showHistoricalData]);

  return (
    <Stack sx={{ textAlign: 'left' }} gap={4}>
      <Typography>Table View Settings</Typography>
      <Stack gap={5}>
        <ButtonGroup aria-label="outlined secondary button group">
          <Button
            startIcon={<CropFreeIcon />}
            sx={{
              fontFamily: 'SF Pro Display',
              backgroundColor:
                matrixZoomLevel === FixedStrikeZoomLevel.Normal
                  ? alpha(theme.palette.secondary.main, 0.25)
                  : 'transparent',
              '&:hover': {
                color: alpha(theme.palette.secondary.main, 1),
                backgroundColor:
                  matrixZoomLevel === FixedStrikeZoomLevel.Normal
                    ? alpha(theme.palette.secondary.main, 0.25)
                    : 'transparent',
                borderColor: alpha(theme.palette.secondary.main, 1),
              },
              fontSize: isMobile ? 12 : 14,
              textTransform: 'none',
              width: '50%',
            }}
            onClick={() => {
              onConfigChange({ zoomLevel: FixedStrikeZoomLevel.Normal });
            }}
          >
            {FixedStrikeZoomLevel.Normal}
          </Button>
          <Button
            startIcon={<ZoomOutMapIcon />}
            sx={{
              fontFamily: 'SF Pro Display',
              backgroundColor:
                matrixZoomLevel === FixedStrikeZoomLevel.ZoomedOut
                  ? alpha(theme.palette.secondary.main, 0.25)
                  : 'transparent',
              '&:hover': {
                color: alpha(theme.palette.secondary.main, 1),
                backgroundColor:
                  matrixZoomLevel === FixedStrikeZoomLevel.ZoomedOut
                    ? alpha(theme.palette.secondary.main, 0.25)
                    : 'transparent',
                borderColor: alpha(theme.palette.secondary.main, 1),
              },
              fontSize: isMobile ? 12 : 14,
              textTransform: 'none',
              width: '50%',
            }}
            onClick={() => {
              onConfigChange({ zoomLevel: FixedStrikeZoomLevel.ZoomedOut });
            }}
          >
            {FixedStrikeZoomLevel.ZoomedOut}
          </Button>
        </ButtonGroup>
        <Stack gap={6} width="100%">
          <Stack direction="row" gap={3} width="100%">
            <FormControl
              sx={{
                marginTop: '5px',
                width: '50%',
              }}
              size="small"
            >
              <InputLabel id="expiration-range-select-label">
                Expiration Range
              </InputLabel>
              <Select
                labelId="expiration-range-select-label"
                id="expirations-range-select"
                value={matrixExpirationRange.toString()}
                label="Expiration Range"
                onChange={handleExpirationChange}
                open={matrixExpirationRangeOpen}
                onOpen={() => setMatrixExpirationRangeOpen(true)}
                onClose={() => setMatrixExpirationRangeOpen(false)}
                size="small"
                input={
                  <OutlinedInput
                    style={{
                      fontSize: isMobile ? 12 : 14,
                    }}
                    sx={{
                      svg: {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                    label="Expiration Range"
                  />
                }
                MenuProps={{
                  sx: {
                    zIndex: 10_001,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: '250px',
                    },
                  },
                }}
              >
                {Array.from(MATRIX_EXPIRATIONS_RANGE_MAPPING.entries()).map(
                  ([range, label]: [MatrixExpirationsRange, string]) => (
                    <MenuItem key={label} value={range}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                marginTop: '5px',
                width: '50%',
              }}
              size="small"
            >
              <InputLabel id="percent-otm-select-label">
                Percent OTM Range
              </InputLabel>
              <Select
                labelId="percent-otm-select-label"
                id="percent-otm-select"
                value={matrixPercentOTMRange.toString()}
                label="Percent OTM Range"
                onChange={handlePercentOTMChange}
                open={matrixPercentOTMRangeOpen}
                onOpen={() => setMatrixPercentOTMRangeOpen(true)}
                onClose={() => setMatrixPercentOTMRangeOpen(false)}
                size="small"
                input={
                  <OutlinedInput
                    style={{
                      fontSize: isMobile ? 12 : 14,
                    }}
                    sx={{
                      svg: {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                    label="Percent OTM Range"
                  />
                }
                MenuProps={{
                  sx: {
                    zIndex: 10_001,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: '250px',
                    },
                  },
                }}
              >
                {Array.from(MATRIX_PERCENT_OTM_RANGE_MAPPING.entries()).map(
                  ([range, label]: [MatrixPercentOTMRange, string]) => (
                    <MenuItem key={label} value={range}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
          <Stack direction="row" gap={3} width="100%">
            <FormControl
              sx={{
                width: '50%',
              }}
              size="small"
            >
              <InputLabel id="prune-expirations-label">
                Prune Expirations
              </InputLabel>
              <Select
                labelId="prune-expirations-label"
                id="prune-expirations-select"
                value={pruneRowsPercent.toString()}
                label="Prune Expirations"
                onChange={handlePruneRowsChange}
                open={pruneRowsPercentSelectorOpen}
                onOpen={() => setPruneRowsPercentSelectorOpen(true)}
                onClose={() => setPruneRowsPercentSelectorOpen(false)}
                size="small"
                input={
                  <OutlinedInput
                    style={{
                      fontSize: isMobile ? 12 : 14,
                    }}
                    sx={{
                      svg: {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                    label="Prune Expirations"
                  />
                }
                MenuProps={{
                  sx: {
                    zIndex: 10_001,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: '200px',
                    },
                  },
                }}
              >
                {Array.from(PRUNE_PERCENT_MAPPING.entries()).map(
                  ([range, label]: [PrunePercentRange, string]) => (
                    <MenuItem key={label} value={range}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>

            <FormControl
              sx={{
                width: '50%',
              }}
              size="small"
            >
              <InputLabel id="prune-strikes-label">Prune Strikes</InputLabel>
              <Select
                labelId="prune-strikes-label"
                id="prune-strikes-select"
                value={pruneColumnsPercent.toString()}
                label="Prune Strikes"
                onChange={handlePruneColumnsChange}
                open={pruneColumnsPercentSelectorOpen}
                onOpen={() => setPruneColumnsPercentSelectorOpen(true)}
                onClose={() => setPruneColumnsPercentSelectorOpen(false)}
                size="small"
                input={
                  <OutlinedInput
                    style={{
                      fontSize: isMobile ? 12 : 14,
                    }}
                    sx={{
                      svg: {
                        color: theme.palette.text.primary,
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5),
                      },
                    }}
                    label="Prune Strikes"
                  />
                }
                MenuProps={{
                  sx: {
                    zIndex: 10_001,
                  },
                  PaperProps: {
                    style: {
                      maxHeight: '200px',
                    },
                  },
                }}
              >
                {Array.from(PRUNE_PERCENT_MAPPING.entries()).map(
                  ([range, label]: [PrunePercentRange, string]) => (
                    <MenuItem key={label} value={range}>
                      {label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Stack>

      <Divider
        sx={{
          borderColor: theme.palette.getContrastText(
            theme.palette.background.default,
          ),
          marginTop: '5px',
          opacity: 0.5,
        }}
        flexItem
      />

      <Stack gap={3}>
        <Stack direction="row" alignItems="center" gap={2}>
          <Typography>Table Data Settings</Typography>
          <InfoPopper
            contents={[
              {
                label: 'Statistical Mode',
                description: MATRIX_DATA_SETTING_TOOLTIPS.statsMode,
              },
              {
                label: 'Statistical look back window',
                description: MATRIX_DATA_SETTING_TOOLTIPS.statsLookback,
              },
              {
                label: 'Skew Premium',
                description: MATRIX_DATA_SETTING_TOOLTIPS.skewPrem,
              },
              {
                label: 'Show Highlights',
                description: MATRIX_DATA_SETTING_TOOLTIPS.highlightsMode,
              },
              {
                label: 'Compare Mode',
                description: MATRIX_DATA_SETTING_TOOLTIPS.compareMode,
              },
              {
                label: 'Z-Score',
                description: MATRIX_DATA_SETTING_TOOLTIPS.zScore,
              },
              {
                label: 'SG IV',
                description: MATRIX_DATA_SETTING_TOOLTIPS.sgIV,
              },
              {
                label: 'Teal Gradient with No Toggles on',
                description: MATRIX_DATA_SETTING_TOOLTIPS.absoluteMode,
              },
            ]}
          />
        </Stack>

        <Grid container rowGap={2} spacing={2}>
          <Grid item xs={isMobile ? 4 : 3}>
            <FormControlLabel
              control={
                <Switch
                  sx={{
                    '& .MuiSwitch-track': {
                      opacity: 0.25,
                      backgroundColor: theme.palette.success.main,
                    },
                    '&.Mui-checked .MuiSwitch-track': {
                      opacity: 1,
                    },
                  }}
                  color="success"
                  checked={isStatsMode}
                  onChange={(_e, checked) =>
                    onToggleDataSettings(MatrixDataMode.STATS_MODE, checked)
                  }
                />
              }
              label={MatrixDataMode.STATS_MODE}
              labelPlacement="top"
            />
          </Grid>
          <Grid item xs={isMobile ? 4 : 3}>
            <FormControlLabel
              control={
                <Switch
                  sx={{
                    '& .MuiSwitch-track': {
                      opacity: 0.25,
                      backgroundColor: theme.palette.success.main,
                    },
                    '&.Mui-checked .MuiSwitch-track': {
                      opacity: 1,
                    },
                  }}
                  color="success"
                  checked={isSkewPremMode}
                  onChange={(_e, checked) =>
                    onToggleDataSettings(
                      MatrixDataMode.SKEW_PREMIUM_MODE,
                      checked,
                    )
                  }
                />
              }
              label={MatrixDataMode.SKEW_PREMIUM_MODE}
              labelPlacement="top"
            />
          </Grid>
          <Grid item xs={isMobile ? 4 : 3}>
            <FormControlLabel
              control={
                <Switch
                  disabled={isCompareMode}
                  sx={{
                    '& .MuiSwitch-track': {
                      opacity: 0.25,
                      backgroundColor: theme.palette.success.main,
                    },
                    '&.Mui-checked .MuiSwitch-track': {
                      opacity: 1,
                    },
                  }}
                  color="success"
                  checked={showHighlights}
                  onChange={(_e, checked) =>
                    onConfigChange({ showHighlights: checked })
                  }
                />
              }
              label="Show Highlights"
              labelPlacement="top"
            />
          </Grid>
          {hasDashAccess(dashAccess, DashAccess.INTERNAL) && (
            <Grid item xs={isMobile ? 4 : 3}>
              <FormControlLabel
                control={
                  <Switch
                    sx={{
                      '& .MuiSwitch-track': {
                        opacity: 0.25,
                        backgroundColor: theme.palette.success.main,
                      },
                      '&.Mui-checked .MuiSwitch-track': {
                        opacity: 1,
                      },
                    }}
                    color="success"
                    checked={showDividends}
                    onChange={(_e, checked) =>
                      onConfigChange({ showDividends: checked })
                    }
                  />
                }
                label="Dividends"
                labelPlacement="top"
              />
            </Grid>
          )}

          {isMobile && (
            <Grid item xs={12}>
              {statsLookbackComponent}
            </Grid>
          )}

          <Grid item xs={isMobile ? 12 : 0.5}>
            <Divider
              sx={{
                borderColor: theme.palette.getContrastText(
                  theme.palette.background.default,
                ),
                marginTop: isMobile ? '5px' : 0,
                opacity: 0.5,
              }}
              orientation={isMobile ? 'horizontal' : 'vertical'}
            />
          </Grid>

          {isMobile && (
            <Grid item xs={12}>
              {showHistoricalDataToggle}
            </Grid>
          )}

          <Grid item xs={isMobile ? 12 : 2.5}>
            {compareModeToggle}
          </Grid>

          {hasDashAccess(dashAccess, DashAccess.INTERNAL) && (
            <Grid item xs={isMobile ? 4 : 3}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={isCompareMode}
                    sx={{
                      '& .MuiSwitch-track': {
                        opacity: 0.25,
                        backgroundColor: theme.palette.success.main,
                      },
                      '&.Mui-checked .MuiSwitch-track': {
                        opacity: 1,
                      },
                    }}
                    color="success"
                    checked={useSpotGammaIV}
                    onChange={onUseSpotGammaIVToggle}
                  />
                }
                label="SpotGamma IV"
                labelPlacement="top"
              />
            </Grid>
          )}

          {!isMobile && (
            <Grid item xs={12}>
              {statsLookbackComponent}
            </Grid>
          )}
        </Grid>
      </Stack>

      <Stack gap={4}>
        {!isMobile && showHistoricalDataToggle}

        {(showHistoricalData || isCompareMode) && (
          <Stack direction="row" alignItems="center" gap={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {showHistoricalData && (
                <DatePicker
                  views={['day']}
                  label="Reference Date"
                  value={currentDate}
                  onChange={onCurrentTradeDateChange}
                  shouldDisableDate={(date: Dayjs) =>
                    !isBusinessDay(date) ||
                    !isMarketOpenOnDate(date) ||
                    (isCompareMode &&
                      (date.isSame(compareDate, 'day') ||
                        date.isBefore(compareDate, 'day')))
                  }
                  format="YYYY-MM-DD"
                  minDate={MIN_QUERY_DATE}
                  maxDate={maxCurrentDate}
                  slotProps={{
                    ...defaultSlotProps,
                    textField: {
                      ...defaultSlotProps.textField,
                      style: {
                        width: '50%',
                        fontSize: '14px',
                      },
                      helperText: currentDateHelperText,
                      error: error != null,
                    },
                  }}
                />
              )}

              {isCompareMode ? (
                <DatePicker
                  views={['day']}
                  label="Comparison Date"
                  value={compareDate}
                  onChange={onPrevTradeDateChange}
                  shouldDisableDate={(date: Dayjs) =>
                    !isBusinessDay(date) ||
                    !isMarketOpenOnDate(date) ||
                    (showHistoricalData &&
                      (date.isSame(currentDate, 'day') ||
                        date.isAfter(currentDate, 'day')))
                  }
                  format="YYYY-MM-DD"
                  minDate={MIN_QUERY_DATE}
                  maxDate={maxCompareDate}
                  slotProps={{
                    ...defaultSlotProps,
                    textField: {
                      ...defaultSlotProps.textField,
                      style: {
                        width: '50%',
                        fontSize: '14px',
                      },
                      helperText: compareDateHelperText,
                      error: error != null,
                    },
                  }}
                />
              ) : null}
            </LocalizationProvider>
          </Stack>
        )}
      </Stack>

      {loading ? (
        <CircularProgress
          style={{
            color: theme.palette.getContrastText(
              theme.palette.background.default,
            ),
            alignSelf: 'flex-end',
          }}
        />
      ) : null}
    </Stack>
  );
};

export const FixedStrikeTableSettings = () => {
  const {
    matrixExpirationRangeOpen,
    matrixPercentOTMRangeOpen,
    pruneRowsPercentSelectorOpen,
    pruneColumnsPercentSelectorOpen,
  } = useFixedStrikeCtx();

  return (
    <SettingsPopout
      title="Fixed Strike Table Settings"
      popperID="fixed-strike-controls"
      placement="bottom-end"
      externalMenuOpen={
        matrixExpirationRangeOpen ||
        matrixPercentOTMRangeOpen ||
        pruneRowsPercentSelectorOpen ||
        pruneColumnsPercentSelectorOpen
      }
      sx={{
        width: {
          xs: '280px',
          md: '400px',
        },
      }}
    >
      <FixedStrikeControls />
    </SettingsPopout>
  );
};
